import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Link, withRouter } from 'react-router-dom';
import { Row, Col, PageHeader, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Features from '../../../shared/Features';
import { Translate } from '../../../shared/LanguageService';
import ProductPropertiesIcons from '../../propertyIcons/ProductPropertiesIcons';
import { CloneOrderButton } from '../cloneOrder/CloneOrderButton';
import * as OrderTypes from './OrderTypes';
import * as Actions from './Actions';
import MoveForm from './MoveOrderForm';
import SwapModal from './planOrders/admin/swap/SwapModal';
import {
    BasicInfoTab,
    ImageTab,
    PlanOrderTab,
    VideoTab,
    LogsTab,
    EditTab,
    TextTab,
    MatterportTab,
    SePlayTab,
    ShowcaseTab,
    DigitalStylingTab
} from './ViewOrderPageTabs';
import ViewOrderPageNavigationBar from './ViewOrderPageNavigationBar';
import * as ProductProperty from '../../../shared/ProductProperties';
import { UserRole } from '../../../shared/types';
import { getSiteName } from '../../../shared/Site';
import * as Site from '../../../shared/Site';
import FailureSnackbar from '../../../shared/feedback/FailureSnackbar';
import { stripRouteHashFromOrderId } from './stripRouteHashFromOrderId';
import { useViewOrderTabNavigation } from './hooks/useViewOrderTabNavigation';
import {
    useViewOrderPageData,
    ViewOrderArchiveState,
    ViewOrderLoadFailure
} from './hooks/useViewOrderPageData';
import { useResetViewOrderOnUnmount } from './hooks/useResetViewOrderOnUnmount';

const productsToExclude = [ProductProperty.BlueSky, ProductProperty.InteriorBlueSky];

const loadFailureTranslationKeys: Record<ViewOrderLoadFailure, string> = {
    'archive-status-check': 'view-order/archive-status-check/failure-snackbar',
    'order-load': 'view-order/order-load/failure-snackbar'
};

export const ViewOrderPage = (props: any) => {
    const dispatch = useDispatch<any>();
    const order = useSelector((state: any) => state.viewOrder.common);
    const user = useSelector((state: any) => state.user);
    const site = useSelector((state: any) => state.site);
    const previousLocation = useSelector((state: any) => state.router.previousLocation);

    const [showSwapPlanOrdersModal, setShowSwapPlanOrdersModal] = React.useState(false);

    const orderId = stripRouteHashFromOrderId(props.match.params.orderId);

    const { activeTab, setActiveTab, showTab, onTabSelect } = useViewOrderTabNavigation(
        props.history,
        props.match,
        props.location
    );

    const selectTabForArchiveStatus = (archiveState: ViewOrderArchiveState): void => {
        if (!archiveState.photoIsArchived && window.location.hash === '#image') {
            showTab('image');
        } else if (!archiveState.planIsArchived && window.location.hash === '#plan') {
            showTab('plan');
        } else if (window.location.hash === '#logs') {
            showTab('logs');
        } else if (
            (order.orderType & OrderTypes.photo) === 0 &&
            (order.orderType & OrderTypes.plan) === 0 &&
            activeTab === ''
        ) {
            showTab('basic');
        }
    };

    const selectTabFromLocationHash = (): void => {
        const tab = window.location.hash.substring(1, window.location.hash.length);
        if (tab) {
            showTab(tab);
        }
    };

    const {
        loading,
        photoIsArchived,
        planIsArchived,
        archivedStatus,
        reloadOrder,
        loadFailure,
        dismissLoadFailure
    } = useViewOrderPageData({
        orderId,
        onArchiveStatusResolved: selectTabForArchiveStatus,
        onOrderLoaded: selectTabFromLocationHash
    });

    useResetViewOrderOnUnmount();

    const assignUser = (): void => {
        dispatch(Actions.assignUser(orderId));
    };

    const getAssignUserButtonText = () => {
        if (!order.assignedUserId) {
            return Translate('view-order/header/assign-user/work');
        } else if (order.assignedUserId && order.assignedUserId !== user.details.id) {
            return Translate('view-order/header/assign-user/take') + order.assignedUserName;
        } else {
            return Translate('view-order/header/assign-user/release');
        }
    };

    const getPropsSiteForUserWithAccess = () => {
        if (site === getSiteName(Site.Zentuvo)) {
            return Site.Zentuvo;
        }
        return Features.hasFeature(Features.DisplaySite, user) === true ? order.site : null;
    };

    const redirectToCreateOrder = (): void => {
        let url = '/orders/new/' + order.orderCollectionId;

        if (site === 'scandphotos') {
            url = '/scandphotos' + url;
        }

        props.history.push(url);
    };

    const displayLegacyOrderNumber = () => {
        if (
            order.legacyOrderNumber != null &&
            order.orderCollectionNumber !== order.legacyOrderNumber
        ) {
            return <span> ({order.legacyOrderNumber})</span>;
        }
    };

    const displayBackButton = (): boolean => {
        if (previousLocation && previousLocation.indexOf('/photographerbooking') >= 0) {
            return false;
        }

        return previousLocation && previousLocation !== props.location.pathname;
    };

    const userRole = user.details.role;
    const orderProductProperties = order.productProperties;

    const isNewOrder = props.location.search.includes('newOrder=1');

    return (
        <div>
            <Row>
                <PageHeader>
                    <Row>
                        <Col xs={12} md={8}>
                            {Translate('view-order/header/left')}
                            <small>
                                {Translate('view-order/header/right')}{' '}
                                {Features.hasFeature(Features.ViewOrderCollection, user) ? (
                                    <Link to={`/ordercollections/${order.orderCollectionId}`}>
                                        {order.orderCollectionNumber}
                                    </Link>
                                ) : (
                                    order.orderCollectionNumber
                                )}
                                :{order.orderNumber}
                                {displayLegacyOrderNumber()}
                            </small>
                            {order.address &&
                                userRole !== UserRole.ImageEditor &&
                                userRole !== UserRole.ImageEditorAdmin &&
                                userRole !== UserRole.PlanEditor &&
                                userRole !== UserRole.PlanEditorAdmin && (
                                    <small>
                                        {' - '}
                                        {order.address}
                                    </small>
                                )}
                            <ProductPropertiesIcons
                                properties={
                                    orderProductProperties && userRole == UserRole.Photographer
                                        ? orderProductProperties.filter((property) => {
                                              return (
                                                  property.value !==
                                                  productsToExclude.find(
                                                      (x) => x === property.value
                                                  )
                                              );
                                          })
                                        : orderProductProperties
                                }
                                site={getPropsSiteForUserWithAccess()}
                                otherOrderId={order.otherOrderId}
                            />
                        </Col>
                        <Col xs={12} md={4} className="view-order-tools">
                            {Features.hasFeature(Features.MoveOrder, user) && (
                                <MoveForm orderId={order.orderId} />
                            )}

                            {Features.hasFeature(
                                Features.CreateOrderInSameOrderCollection,
                                user
                            ) &&
                                order.orderCollectionId && (
                                    <Button onClick={redirectToCreateOrder}>
                                        {Translate('view-order/header/create-new-order')}
                                    </Button>
                                )}

                            {Features.hasFeature(Features.OrderAssignUser, user) &&
                                order.orderId && (
                                    <Button onClick={assignUser}>
                                        {getAssignUserButtonText()}
                                    </Button>
                                )}

                            {Features.hasFeature(Features.CloneOrders, user) &&
                                (order.orderType & OrderTypes.photo) !== 0 && (
                                    <CloneOrderButton orderId={order.orderId} />
                                )}

                            {displayBackButton() &&
                                (user.details.role === 'role/styling-admin' ? (
                                    <Link
                                        to={'/stylingcalendars'}
                                        className="btn btn-default pull-right"
                                    >
                                        {Translate('view-order/header/back-button')}
                                    </Link>
                                ) : (
                                    <Link
                                        to={previousLocation}
                                        className="btn btn-default pull-right"
                                    >
                                        {Translate('view-order/header/back-button')}
                                    </Link>
                                ))}

                            {(order.orderType & OrderTypes.plan) !== 0 &&
                                Features.hasFeature(Features.SwapPlanOrders, user) && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id="swap-buttons-open-tooltip">
                                                <p>
                                                    {Translate(
                                                        'order/plan/swap/buttons/open/tooltip'
                                                    )}
                                                </p>
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            bsStyle="default"
                                            className="pull-right"
                                            onClick={() => setShowSwapPlanOrdersModal(true)}
                                        >
                                            <i className="material-icons no-label md-18">
                                                swap_horiz
                                            </i>
                                        </Button>
                                    </OverlayTrigger>
                                )}
                        </Col>
                    </Row>
                </PageHeader>
            </Row>
            <Row>
                <ViewOrderPageNavigationBar
                    activeTab={activeTab}
                    onSelect={(key) => onTabSelect(key)}
                    user={user}
                    order={order}
                    photoIsArchived={photoIsArchived}
                    planIsArchived={planIsArchived}
                    hasCopywriterFeaturesEnabled={props.hasCopywriterFeaturesEnabled}
                    userRole={userRole}
                />
            </Row>
            <Row id="view-order-tabs">
                <Route
                    path={`${props.match.path}/edit`}
                    render={(routeProps: any) => (
                        <EditTab
                            {...routeProps}
                            orderId={orderId}
                            user={user}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/basic`}
                    render={(routeProps: any) => (
                        <BasicInfoTab
                            {...routeProps}
                            orderId={orderId}
                            user={user}
                            photoIsArchived={photoIsArchived}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/digital-styling`}
                    render={() => (
                        <DigitalStylingTab
                            orderId={props.match.params.orderId}
                            user={user}
                            setActiveTab={setActiveTab}
                            isNewOrder={isNewOrder}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/video`}
                    render={(routeProps: any) => (
                        <VideoTab
                            {...routeProps}
                            orderId={orderId}
                            user={user}
                            order={order}
                            photoIsArchived={photoIsArchived}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/showcase`}
                    render={(routeProps: any) => (
                        <ShowcaseTab
                            {...routeProps}
                            photoIsArchived={photoIsArchived}
                            orderId={orderId}
                            order={order}
                            setActiveTab={setActiveTab}
                            archivedStatus={archivedStatus}
                            reloadData={reloadOrder}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/image`}
                    render={(routeProps: any) => (
                        <ImageTab
                            {...routeProps}
                            photoIsArchived={photoIsArchived}
                            orderId={orderId}
                            order={order}
                            setActiveTab={setActiveTab}
                            archivedStatus={archivedStatus}
                            reloadData={reloadOrder}
                            user={user}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/text`}
                    render={(routeProps: any) => (
                        <TextTab
                            {...routeProps}
                            orderId={orderId}
                            user={user}
                            order={order}
                            photoIsArchived={photoIsArchived}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/plan`}
                    render={(routeProps: any) => (
                        <PlanOrderTab
                            {...routeProps}
                            planIsArchived={planIsArchived}
                            loading={loading}
                            user={user}
                            orderId={orderId}
                            isMeasured={order.isMeasured}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/matterport`}
                    render={(routeProps: any) => (
                        <MatterportTab
                            {...routeProps}
                            orderId={orderId}
                            user={user}
                            order={order}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/seplay`}
                    render={(routeProps: any) => (
                        <SePlayTab
                            {...routeProps}
                            orderId={orderId}
                            user={user}
                            order={order}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
                <Route
                    path={`${props.match.path}/logs`}
                    render={(routeProps: any) => (
                        <LogsTab
                            {...routeProps}
                            loading={loading}
                            user={user}
                            orderId={orderId}
                            setActiveTab={setActiveTab}
                        />
                    )}
                />
            </Row>
            {showSwapPlanOrdersModal && (
                <SwapModal
                    orderId={props.match.params.orderId}
                    close={(otherOrderId?: number) => {
                        setShowSwapPlanOrdersModal(false);
                        if (otherOrderId) {
                            props.history.push(`/orders/${otherOrderId}/plan`);
                        }
                    }}
                />
            )}
            <FailureSnackbar
                open={loadFailure !== null}
                onClose={dismissLoadFailure}
                text={loadFailure !== null ? Translate(loadFailureTranslationKeys[loadFailure]) : ''}
            />
        </div>
    );
};

export default withRouter(ViewOrderPage);
